import { useState, useEffect } from 'react';
import { useBodyClass } from '@/hooks/useBodyClass';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, Plus, Search, X, Edit2, Trash2,
  ChefHat, Apple, Sparkles, Loader2, Check, History as HistoryIcon, Star, Clock,
} from 'lucide-react';
import { Button } from '@/components/Button';
import { Loader } from '@/components/Loader';
import { useTelegram } from '@/hooks/useTelegram';
import {
  useUserFavorites,
  deleteFavorite,
  addProductToFavorites,
} from '@/hooks/useFavorites';
import { FavoriteCard } from '@/components/FavoriteCard';
import { LogFavoriteModal } from '@/components/LogFavoriteModal';
import { LogHistoryItemModal } from '@/components/LogHistoryItemModal';
import { useFoodHistory, type HistoryItem, type HistoryMeal } from '@/hooks/useFoodHistory';
import { CopyMealModal } from '@/components/CopyMealModal';
import { HistoryItemCard, HistoryMealCard } from '@/components/HistoryCards';
import { RecipeForm } from '@/components/RecipeForm';
import { analyzeText } from '@/lib/ai';
import type { Favorite } from '@/types';

// 🆕 Добавлен таб 'history'
type FilterTab = 'all' | 'recipes' | 'products' | 'history';
type ViewMode = 'list' | 'create' | 'edit' | 'create_product';
type HistorySubTab = 'top' | 'recent';

export default function FavoriteList() {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { haptic } = useTelegram();
  
  // 🆕 Поддержка URL ?tab=history
  const initialTab = (searchParams.get('tab') as FilterTab) || 'all';
  const [filter, setFilter] = useState<FilterTab>(initialTab);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editingRecipeId, setEditingRecipeId] = useState<string | null>(null);
  const [selectedForLog, setSelectedForLog] = useState<Favorite | null>(null);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState<HistoryItem | null>(null);
  const [selectedHistoryMeal, setSelectedHistoryMeal] = useState<HistoryMeal | null>(null);
  const [actionsFor, setActionsFor] = useState<Favorite | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  
  // 🆕 Подвкладка для истории
  const [historySubTab, setHistorySubTab] = useState<HistorySubTab>('top');

  // Загрузка favorites — только для табов кроме history
  const favoritesFilter = filter === 'history' ? 'all' : filter;
  const { favorites, loading: favLoading, refetch: refetchFavorites } = useUserFavorites(favoritesFilter as 'all' | 'recipes' | 'products');
  
  // 🆕 Загрузка истории — только когда таб history активен
  const { topItems, recentMeals, loading: historyLoading, refetch: refetchHistory } = useFoodHistory();

  // Обновляем URL при смене таба
  useEffect(() => {
    if (filter === 'history') {
      setSearchParams({ tab: 'history' }, { replace: true });
    } else if (searchParams.get('tab')) {
      setSearchParams({}, { replace: true });
    }
  }, [filter]);

  // Фильтрация по поиску
  const filteredFavorites = favorites.filter((f) => {
    if (!searchQuery.trim()) return true;
    return f.name.toLowerCase().includes(searchQuery.toLowerCase().trim());
  });
  
  const filteredHistoryItems = topItems.filter((i) => {
    if (!searchQuery.trim()) return true;
    return i.name.toLowerCase().includes(searchQuery.toLowerCase().trim());
  });
  
  const filteredHistoryMeals = recentMeals.filter((m) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase().trim();
    return m.item_names.some(n => n.toLowerCase().includes(q));
  });

  function handleSelectFavorite(favorite: Favorite) { setSelectedForLog(favorite); }
  function handleSelectHistoryItem(item: HistoryItem) { setSelectedHistoryItem(item); }
  function handleActionsClick(favorite: Favorite) { setActionsFor(favorite); }

  function handleEditRecipe() {
    if (!actionsFor) return;
    setEditingRecipeId(actionsFor.id);
    setViewMode('edit');
    setActionsFor(null);
  }

  async function handleDelete() {
    if (!actionsFor) return;
    const name = actionsFor.name;
    const isRecipe = actionsFor.is_recipe;
    if (!confirm(isRecipe
      ? `Удалить рецепт «${name}»?\n\nЗаписи в дневнике сохранятся, но связь с рецептом пропадёт.`
      : `Удалить «${name}» из избранного?`
    )) { setActionsFor(null); return; }
    setDeletingId(actionsFor.id);
    haptic('medium');
    try {
      const result = await deleteFavorite(actionsFor.id);
      if (result.success) { haptic('success'); await refetchFavorites(); }
      else { haptic('error'); alert(`Не удалось удалить: ${result.error ?? 'ошибка'}`); }
    } finally {
      setDeletingId(null);
      setActionsFor(null);
    }
  }

  function handleNewRecipe() { haptic('medium'); setViewMode('create'); setEditingRecipeId(null); }
  function handleNewProduct() { haptic('medium'); setViewMode('create_product'); }

  function handleRecipeSaved() { setViewMode('list'); setEditingRecipeId(null); refetchFavorites(); }
  function handleFormCancel() { setViewMode('list'); setEditingRecipeId(null); }

  // ── Режим создания/редактирования рецепта ──
  if (viewMode === 'create' || viewMode === 'edit') {
    return (
      <RecipeForm
        recipeId={viewMode === 'edit' ? editingRecipeId ?? undefined : undefined}
        onSaved={handleRecipeSaved}
        onCancel={handleFormCancel}
      />
    );
  }

  // ── Режим создания продукта ──
  if (viewMode === 'create_product') {
    return (
      <ProductForm
        onSaved={() => { setViewMode('list'); refetchFavorites(); }}
        onCancel={() => setViewMode('list')}
      />
    );
  }

  const isHistoryTab = filter === 'history';
  const loading = isHistoryTab ? historyLoading : favLoading;

  // ── Основной список ──
  return (
    <div className="px-5 pt-4 pb-28 max-w-md mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <button
          type="button"
          onClick={() => nav(-1)}
          className="w-10 h-10 rounded-xl bg-bg-card border border-line flex items-center justify-center"
        >
          <ChevronLeft size={20} />
        </button>
        <h1 className="font-display text-xl font-bold flex-1">Мои блюда</h1>
        <button
          type="button"
          onClick={() => { setShowSearch(!showSearch); if (showSearch) setSearchQuery(''); haptic('light'); }}
          className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${
            showSearch ? 'bg-emerald-glow/15 text-emerald-glow' : 'bg-bg-card border border-line text-ink-muted'
          }`}
        >
          <Search size={18} />
        </button>
      </div>

      {/* Поиск */}
      <AnimatePresence>
        {showSearch && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden mb-4"
          >
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Найти блюдо или продукт..."
                className="input pl-9 pr-9"
                autoFocus
              />
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-dim pointer-events-none" />
              {searchQuery && (
                <button type="button" onClick={() => { setSearchQuery(''); haptic('light'); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-dim hover:text-ink">
                  <X size={14} />
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 🆕 Фильтры — теперь 4 таба */}
      <div className="card-elevated p-1 grid grid-cols-4 mb-4">
        {([
          { k: 'all', label: 'Все', Icon: null },
          { k: 'recipes', label: 'Рецепты', Icon: ChefHat },
          { k: 'products', label: 'Продукты', Icon: Apple },
          { k: 'history', label: 'История', Icon: HistoryIcon },
        ] as const).map(({ k, label, Icon }) => (
          <button
            type="button"
            key={k}
            onClick={() => { setFilter(k); haptic('light'); }}
            className={`flex items-center justify-center gap-1 py-2 rounded-lg text-[11px] transition-all ${
              filter === k ? 'bg-bg text-ink shadow-card font-medium' : 'text-ink-muted'
            }`}
          >
            {Icon && <Icon size={11} />} {label}
          </button>
        ))}
      </div>

      {/* Кнопки создания / Подвкладки истории */}
      {filter === 'history' ? (
        // 🆕 ПОДВКЛАДКИ ИСТОРИИ
        <div className="grid grid-cols-2 gap-2 mb-4">
          <button
            type="button"
            onClick={() => { setHistorySubTab('top'); haptic('light'); }}
            className={`py-2.5 rounded-xl text-xs font-medium transition-all flex items-center justify-center gap-1.5 ${
              historySubTab === 'top'
                ? 'bg-sky-glow/15 text-sky-glow border border-sky-glow/30'
                : 'bg-bg-card text-ink-muted border border-line'
            }`}
          >
            <Star size={11} />
            Часто едите
            {topItems.length > 0 && (
              <span className="text-[10px] opacity-60">({topItems.length})</span>
            )}
          </button>
          <button
            type="button"
            onClick={() => { setHistorySubTab('recent'); haptic('light'); }}
            className={`py-2.5 rounded-xl text-xs font-medium transition-all flex items-center justify-center gap-1.5 ${
              historySubTab === 'recent'
                ? 'bg-sky-glow/15 text-sky-glow border border-sky-glow/30'
                : 'bg-bg-card text-ink-muted border border-line'
            }`}
          >
            <Clock size={11} />
            Последние приёмы
            {recentMeals.length > 0 && (
              <span className="text-[10px] opacity-60">({recentMeals.length})</span>
            )}
          </button>
        </div>
      ) : filter === 'products' ? (
        <button
          type="button"
          onClick={handleNewProduct}
          className="w-full mb-4 py-3 rounded-xl border-2 border-dashed border-sky-glow/40 bg-sky-glow/5 hover:bg-sky-glow/10 active:scale-[0.98] transition-all flex items-center justify-center gap-2 text-sky-glow text-sm font-medium"
        >
          <Plus size={16} />
          Добавить продукт
        </button>
      ) : filter === 'recipes' ? (
        <button
          type="button"
          onClick={handleNewRecipe}
          className="w-full mb-4 py-3 rounded-xl border-2 border-dashed border-emerald-glow/40 bg-emerald-glow/5 hover:bg-emerald-glow/10 active:scale-[0.98] transition-all flex items-center justify-center gap-2 text-emerald-glow text-sm font-medium"
        >
          <Plus size={16} />
          Создать новый рецепт
        </button>
      ) : (
        // Вкладка "Все" — две кнопки
        <div className="grid grid-cols-2 gap-2 mb-4">
          <button
            type="button"
            onClick={handleNewRecipe}
            className="py-3 rounded-xl border-2 border-dashed border-emerald-glow/40 bg-emerald-glow/5 hover:bg-emerald-glow/10 active:scale-[0.98] transition-all flex items-center justify-center gap-2 text-emerald-glow text-xs font-medium"
          >
            <ChefHat size={14} />
            Рецепт
          </button>
          <button
            type="button"
            onClick={handleNewProduct}
            className="py-3 rounded-xl border-2 border-dashed border-sky-glow/40 bg-sky-glow/5 hover:bg-sky-glow/10 active:scale-[0.98] transition-all flex items-center justify-center gap-2 text-sky-glow text-xs font-medium"
          >
            <Apple size={14} />
            Продукт
          </button>
        </div>
      )}

      {/* Контент */}
      {loading ? (
        <Loader label={isHistoryTab ? "Загружаем историю..." : "Загружаем твои блюда..."} />
      ) : isHistoryTab ? (
        // 🆕 КОНТЕНТ ВКЛАДКИ ИСТОРИЯ
        historySubTab === 'top' ? (
          filteredHistoryItems.length === 0 ? (
            <HistoryEmptyState type="items" searchActive={!!searchQuery} />
          ) : (
            <div className="space-y-2">
              {filteredHistoryItems.map((item) => (
                <HistoryItemCard
                  key={item.name}
                  item={item}
                  onSelect={handleSelectHistoryItem}
                />
              ))}
            </div>
          )
        ) : (
          filteredHistoryMeals.length === 0 ? (
            <HistoryEmptyState type="meals" searchActive={!!searchQuery} />
          ) : (
            <div className="space-y-2">
              {filteredHistoryMeals.map((meal) => (
  <HistoryMealCard
    key={meal.meal_id}
    meal={meal}
    onCopyClick={setSelectedHistoryMeal}
  />
))}
            </div>
          )
        )
      ) : filteredFavorites.length === 0 ? (
        <EmptyState
          filter={filter}
          searchActive={!!searchQuery}
          onCreateRecipe={handleNewRecipe}
          onCreateProduct={handleNewProduct}
        />
      ) : (
        <div className="space-y-2">
          {filteredFavorites.map((fav) => (
            <FavoriteCard
              key={fav.id}
              favorite={fav}
              onSelect={handleSelectFavorite}
              onActionsClick={handleActionsClick}
            />
          ))}
        </div>
      )}

      {/* Модалки */}
      <LogFavoriteModal
        open={!!selectedForLog}
        onClose={() => setSelectedForLog(null)}
        favorite={selectedForLog}
        onLogged={() => { refetchFavorites(); }}
      />

      {/* 🆕 Модалка для добавления из истории */}
      <LogHistoryItemModal
        open={!!selectedHistoryItem}
        onClose={() => setSelectedHistoryItem(null)}
        item={selectedHistoryItem}
        onLogged={() => { 
          setSelectedHistoryItem(null);
          refetchHistory();
        }}
        onSavedToFavorites={() => {
          refetchFavorites();
        }}
      />
      <CopyMealModal
  open={!!selectedHistoryMeal}
  onClose={() => setSelectedHistoryMeal(null)}
  meal={selectedHistoryMeal}
  onCopied={() => {
    setSelectedHistoryMeal(null);
    refetchHistory();
  }}
/>

      <ActionsMenu
        open={!!actionsFor}
        favorite={actionsFor}
        onClose={() => setActionsFor(null)}
        onEdit={handleEditRecipe}
        onDelete={handleDelete}
        isDeleting={!!deletingId}
      />
    </div>
  );
}

// ============================================
// 🆕 EMPTY STATE ДЛЯ ИСТОРИИ
// ============================================

function HistoryEmptyState({ 
  type, 
  searchActive 
}: { 
  type: 'items' | 'meals'; 
  searchActive: boolean 
}) {
  if (searchActive) {
    return (
      <div className="text-center py-12 px-4">
        <Search size={32} className="mx-auto text-ink-dim mb-3" />
        <div className="font-display font-semibold mb-1">Ничего не найдено</div>
        <div className="text-sm text-ink-dim">Попробуй другое название</div>
      </div>
    );
  }
  
  return (
    <div className="text-center py-10 px-4">
      <div className="text-5xl mb-4">
        {type === 'items' ? '🍽' : '📅'}
      </div>
      <div className="font-display font-bold text-lg mb-2">
        {type === 'items' ? 'История пуста' : 'Приёмов пока нет'}
      </div>
      <div className="text-sm text-ink-dim leading-relaxed">
        {type === 'items' 
          ? 'Добавь несколько приёмов пищи — и здесь появятся продукты которые ты часто ешь.'
          : 'Записывай приёмы пищи — и здесь появится возможность копировать их одним тапом.'}
      </div>
    </div>
  );
}

// ============================================
// ФОРМА ДОБАВЛЕНИЯ ПРОДУКТА (без изменений)
// ============================================
function ProductForm({
  onSaved,
  onCancel,
}: {
  onSaved: () => void;
  onCancel: () => void;
}) {
  const { haptic } = useTelegram();
  const [name, setName] = useState('');
  const [emoji, setEmoji] = useState('🍽');
  const [calories, setCalories] = useState('');
  const [protein, setProtein] = useState('');
  const [fat, setFat] = useState('');
  const [carbs, setCarbs] = useState('');
  const [saving, setSaving] = useState(false);

  const [aiInput, setAiInput] = useState('');
  const [analyzing, setAnalyzing] = useState(false);

  async function handleAI() {
    if (!aiInput.trim()) return;
    setAnalyzing(true);
    haptic('medium');
    try {
      const result = await analyzeText(aiInput.trim());
      const item = result.items?.[0];
      if (!item) { haptic('error'); alert('Не удалось распознать. Попробуй переформулировать.'); return; }
      
      const w = item.estimated_weight_g || 100;
      const factor = 100 / w;
      setName(item.name);
      setCalories(String(Math.round((item.calories ?? 0) * factor)));
      setProtein(String(+((item.protein_g ?? 0) * factor).toFixed(1)));
      setFat(String(+((item.fat_g ?? 0) * factor).toFixed(1)));
      setCarbs(String(+((item.carbs_g ?? 0) * factor).toFixed(1)));
      setAiInput('');
      haptic('success');
    } catch {
      haptic('error');
      alert('Ошибка. Попробуй ещё раз.');
    } finally {
      setAnalyzing(false);
    }
  }

  async function handleSave() {
    if (!name.trim()) { alert('Введи название продукта'); return; }
    if (!calories) { alert('Введи калорийность на 100г'); return; }
    setSaving(true);
    haptic('medium');
    try {
      const result = await addProductToFavorites({
        name: name.trim(),
        emoji,
        calories_per_100g: Number(calories),
        protein_per_100g: Number(protein) || 0,
        fat_per_100g: Number(fat) || 0,
        carbs_per_100g: Number(carbs) || 0,
      });
      if (!result.success) throw new Error(result.error);
      haptic('success');
      onSaved();
    } catch (e: any) {
      haptic('error');
      alert(`Не удалось сохранить: ${e.message}`);
    } finally {
      setSaving(false);
    }
  }

  const EMOJI_PRESETS = ['🍎','🍌','🥚','🧀','🥩','🍗','🐟','🥛','🥦','🥕','🍞','🥜','🫙','🫐','🍳','🥗'];

  return (
    <div className="px-5 pt-4 pb-32 max-w-md mx-auto">
      <div className="flex items-center gap-3 mb-5">
        <button
          type="button"
          onClick={onCancel}
          className="w-10 h-10 rounded-xl bg-bg-card border border-line flex items-center justify-center"
        >
          <ChevronLeft size={20} />
        </button>
        <h1 className="font-display text-xl font-bold flex-1">Новый продукт</h1>
      </div>

      <div className="card p-4 mb-4">
        <div className="text-xs text-ink-dim mb-2">Заполнить через ИИ</div>
        <div className="flex gap-2">
          <input
            type="text"
            value={aiInput}
            onChange={(e) => setAiInput(e.target.value)}
            placeholder="например: творог 5% жирности"
            className="input flex-1 text-sm"
            onKeyDown={(e) => { if (e.key === 'Enter' && !analyzing) handleAI(); }}
          />
          <Button
            size="md"
            onClick={handleAI}
            disabled={!aiInput.trim() || analyzing}
            icon={analyzing ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          >
            {analyzing ? '…' : 'Найти'}
          </Button>
        </div>
        <p className="text-[11px] text-ink-dim mt-2">
          ИИ заполнит КБЖУ автоматически — ты только проверишь
        </p>
      </div>

      <div className="card-elevated p-4 mb-4">
        <div className="flex items-start gap-3 mb-3">
          <div className="text-3xl w-14 h-14 rounded-2xl bg-bg-elevated flex items-center justify-center shrink-0">
            {emoji}
          </div>
          <div className="flex-1">
            <label className="label mb-1 block">Название продукта</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Творог 5%"
              className="input"
              maxLength={50}
            />
          </div>
        </div>
        <div className="grid grid-cols-8 gap-1">
          {EMOJI_PRESETS.map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => { setEmoji(e); haptic('light'); }}
              className={`w-9 h-9 rounded-lg text-xl flex items-center justify-center transition-colors ${
                emoji === e ? 'bg-emerald-glow/20 ring-1 ring-emerald-glow' : 'hover:bg-bg-elevated/60'
              }`}
            >
              {e}
            </button>
          ))}
        </div>
      </div>

      <div className="card p-4 mb-4">
        <label className="label mb-3 block">КБЖУ на 100г</label>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <div className="text-[10px] text-ink-dim mb-1">Калории *</div>
            <input
              type="number" inputMode="numeric"
              placeholder="ккал"
              className="input text-sm py-2"
              value={calories}
              onChange={(e) => setCalories(e.target.value)}
            />
          </div>
          <div>
            <div className="text-[10px] text-emerald-glow mb-1">Белки, г</div>
            <input
              type="number" inputMode="decimal"
              placeholder="0"
              className="input text-sm py-2"
              value={protein}
              onChange={(e) => setProtein(e.target.value)}
            />
          </div>
          <div>
            <div className="text-[10px] text-warn mb-1">Жиры, г</div>
            <input
              type="number" inputMode="decimal"
              placeholder="0"
              className="input text-sm py-2"
              value={fat}
              onChange={(e) => setFat(e.target.value)}
            />
          </div>
          <div>
            <div className="text-[10px] text-sky-glow mb-1">Углеводы, г</div>
            <input
              type="number" inputMode="decimal"
              placeholder="0"
              className="input text-sm py-2"
              value={carbs}
              onChange={(e) => setCarbs(e.target.value)}
            />
          </div>
        </div>
        {calories && (
          <div className="mt-3 bg-emerald-glow/5 border border-emerald-glow/20 rounded-lg px-3 py-2 text-xs flex items-center gap-3">
            <span className="font-bold text-emerald-glow">{calories} ккал</span>
            {protein && <span className="text-emerald-glow">Б {protein}г</span>}
            {fat && <span className="text-warn">Ж {fat}г</span>}
            {carbs && <span className="text-sky-glow">У {carbs}г</span>}
            <span className="text-ink-dim ml-auto">/ 100г</span>
          </div>
        )}
      </div>

      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="fixed bottom-16 left-0 right-0 z-40 px-5 pb-3 pt-3 bg-gradient-to-t from-bg via-bg/95 to-bg/0 max-w-md mx-auto"
        style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
      >
        <Button
          fullWidth size="lg"
          disabled={saving || !name.trim() || !calories}
          onClick={handleSave}
          icon={saving ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
          className="shadow-2xl"
        >
          {saving ? 'Сохраняем…' : 'Сохранить продукт'}
        </Button>
      </motion.div>
    </div>
  );
}

// ============================================
// EMPTY STATE (без изменений)
// ============================================
function EmptyState({
  filter,
  searchActive,
  onCreateRecipe,
  onCreateProduct,
}: {
  filter: FilterTab;
  searchActive: boolean;
  onCreateRecipe: () => void;
  onCreateProduct: () => void;
}) {
  if (searchActive) {
    return (
      <div className="text-center py-12 px-4">
        <Search size={32} className="mx-auto text-ink-dim mb-3" />
        <div className="font-display font-semibold mb-1">Ничего не найдено</div>
        <div className="text-sm text-ink-dim">Попробуй другое название</div>
      </div>
    );
  }
  
  if (filter === 'products') {
    return (
      <div className="text-center py-10 px-4">
        <div className="text-5xl mb-4">🍎</div>
        <div className="font-display font-bold text-lg mb-2">Нет любимых продуктов</div>
        <div className="text-sm text-ink-dim mb-6 leading-relaxed">
          Сохраняй продукты которые часто ешь — например, утренний творог или орехи для перекуса.
        </div>
        <Button size="md" onClick={onCreateProduct} icon={<Apple size={14} />}>
          Добавить первый продукт
        </Button>
        <div className="mt-6 card p-3 text-left">
          <div className="text-xs text-ink-dim flex items-start gap-2">
            <span className="text-sky-glow shrink-0">💡</span>
            <div>
              <div className="font-medium text-ink mb-1">Например:</div>
              <div className="space-y-0.5 text-[11px]">
                <div>🥚 Яйцо куриное</div>
                <div>🧀 Творог 5%</div>
                <div>🥜 Миндаль</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  if (filter === 'recipes') {
    return (
      <div className="text-center py-10 px-4">
        <div className="text-5xl mb-4">🍲</div>
        <div className="font-display font-bold text-lg mb-2">Нет рецептов</div>
        <div className="text-sm text-ink-dim mb-6 leading-relaxed">
          Создай рецепт своего любимого блюда — потом добавляй в дневник одним тапом.
        </div>
        <Button size="md" onClick={onCreateRecipe} icon={<Sparkles size={14} />}>
          Создать первый рецепт
        </Button>
      </div>
    );
  }
  
  return (
    <div className="text-center py-10 px-4">
      <div className="text-5xl mb-4">🍽</div>
      <div className="font-display font-bold text-lg mb-2">Тут пока пусто</div>
      <div className="text-sm text-ink-dim mb-6 leading-relaxed">
        Создай рецепт или сохрани продукты которые часто ешь — потом добавляй одним тапом.
      </div>
      <div className="flex gap-2 justify-center">
        <Button size="md" onClick={onCreateRecipe} icon={<ChefHat size={14} />}>
          Рецепт
        </Button>
        <Button size="md" variant="secondary" onClick={onCreateProduct} icon={<Apple size={14} />}>
          Продукт
        </Button>
      </div>
    </div>
  );
}

// ============================================
// МЕНЮ ДЕЙСТВИЙ (без изменений)
// ============================================
function ActionsMenu({
  open, favorite, onClose, onEdit, onDelete, isDeleting,
}: {
  open: boolean;
  favorite: Favorite | null;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  isDeleting: boolean;
}) {
  // Экраны лежат в слое ниже нижней панели (z-10 против z-30): без этого панель
  // закрывает низ выезжающего окна вместе с его кнопками (боевой случай 10.10.2026).
  useBodyClass('modal-open', open && !!favorite);
  if (!favorite) return null;
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 350, damping: 30 }}
            className="fixed bottom-0 left-0 right-0 z-50 bg-bg rounded-t-3xl"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          >
            <div className="px-5 pt-5 pb-12 max-w-md mx-auto">
              <div className="w-12 h-1 bg-line rounded-full mx-auto mb-4" />
              <div className="flex items-center gap-3 mb-4">
                <div className="text-3xl">{favorite.emoji || '🍽'}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold truncate">{favorite.name}</div>
                  <div className="text-[11px] text-ink-dim">{favorite.is_recipe ? 'Рецепт' : 'Продукт'}</div>
                </div>
              </div>
              <div className="space-y-2">
                {favorite.is_recipe && (
                  <button
                    type="button" onClick={onEdit} disabled={isDeleting}
                    className="w-full p-3 rounded-xl bg-bg-card border border-line hover:bg-bg-elevated/60 active:scale-[0.98] transition-all flex items-center gap-3 text-left disabled:opacity-50"
                  >
                    <div className="w-10 h-10 rounded-lg bg-emerald-glow/15 text-emerald-glow flex items-center justify-center">
                      <Edit2 size={16} />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-sm">Редактировать рецепт</div>
                      <div className="text-[11px] text-ink-dim">Изменить ингредиенты или порции</div>
                    </div>
                  </button>
                )}
                <button
                  type="button" onClick={onDelete} disabled={isDeleting}
                  className="w-full p-3 rounded-xl bg-danger/5 border border-danger/30 hover:bg-danger/10 active:scale-[0.98] transition-all flex items-center gap-3 text-left disabled:opacity-50"
                >
                  <div className="w-10 h-10 rounded-lg bg-danger/15 text-danger flex items-center justify-center">
                    {isDeleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-sm text-danger">Удалить</div>
                    <div className="text-[11px] text-ink-dim">
                      {favorite.is_recipe ? 'Записи в дневнике сохранятся' : 'Убрать из избранного'}
                    </div>
                  </div>
                </button>
              </div>
              <button
                type="button" onClick={onClose} disabled={isDeleting}
                className="w-full mt-4 py-3 rounded-xl text-ink-muted text-sm hover:text-ink transition-colors"
              >
                Отмена
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
